import React, { useContext, memo } from 'react';
import styled from 'styled-components';

import { fieldHelper } from '@builttocreate/engine-utils';

import FieldTypes from '../../../constants/FieldTypes';
import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';
import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';
import FieldModes from '../../../constants/FieldModes';
import FileViews from '../../../constants/FileViews';
import PageLayoutModes from '../../../constants/PageLayoutModes';

import { renderValidation } from '../../../lib/validation';
import { resolveFieldRequired, resolveColumnRequired } from '../../../lib/requiredLogic';
import { isFieldHiddenInView } from '../../../lib/documentHelper';
import { resolveAutoShrinkConfig } from '../../../lib/autoShrinkHelper';
import { resolveAutoHeightEnabled } from '../../../lib/autoHeightHelper';
import { resolveEditability } from '../../../lib/tableHelper';

import TextField from './TextField';
import DateField from './DateField';
import BlockField from './BlockField';
import TextareaField from './TextareaField';
import ImageField from './ImageField';
import FileField from './FileField';
import RTEField from './RTEField';
import RichTextField from './rte/RichTextField';
import DropdownField from './DropdownGenericField';
import DropdownSelectionField from './DropdownSelectionField';
import SignatureField from './SignatureField';
import MultiSelectField from './MultiSelectField';
import MultiSelectSelectionField from './MultiSelectSelectionField';
import ChartField from './chart/ChartField';
import CollectionField from './collection/CollectionField';
import PricingTableField from './pricing/PricingTableField';

import TableFieldv2 from './table/TableFieldv2';
import TableTextField from './table/TableTextField';
import TableNumberField from './table/TableNumberField';
import TableImageField from './table/TableImageField';
import TableDropdownField from './table/TableDropdownField';
import TableDropdownSelectionField from './table/TableDropdownSelectionField';
import TableMultiselectField from './table/TableMultiselectField';
import TableDateField from './table/TableDateField';
import TableBlockField from './table/TableBlockField';
import TableBarcodeField from './table/TableBarcodeField';
import TableSignatureField from './table/TableSignatureField';

import Icon from '../../misc/Icon';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';
import { getFieldThemeObject } from '../../../theme/getThemeObject';

const { isFieldEmpty } = fieldHelper;

/**
 * <-----------------------NOTICE------------------>
 * Overflow is visible in readOnly mode to ensure that the field value
 * appears fully without being cut off due to inadequate field height
 */
const StyledFieldWrapper = styled.div`
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  overflow: ${(props) => (props.disabled ? 'visible' : ' hidden')};
`;

const StyledField = styled.div`
  height: 100%;
`;

const StyledFieldIndicatorWrapper = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  bottom: -6px;
  left: -4px;
`;

const StyledFieldIndicator = styled.div`
  width: 15px;
  height: 15px;
  text-align: center;
  border-radius: 100px;
  border-color: transparent;
  z-index:1;
  margin-right: 2px;
  cursor: default;
  svg {
    font-size: 14px;
    vertical-align: 0;
  }
`;

const Field = (props) => {

  const {
    _id,
    field,
    identifier,
    config,
    type,
    view,
    displayType,
    columnType,
    disabled,
    required,
    hidden,
    hiddenViews,
    options,
    cellValue,
    editability,

    fieldMode,
    indicatorMode,
    fieldPresentationMode,
    pageLayoutMode,
    fieldDefaultStyles,

    /**
     * Properties used for adjusting fields
     * for PDF Export and Page Breaks. Do not
     * remove.
     */
    layouts,
    licenseValidation,

    /**
     * Auto-height. Pulled out here so it can be withheld from every field type that is not
     * text or textarea, and from the exporter, before it reaches anything that spreads props.
     */
    onAutoHeightChange,

  } = props;

  /**
   * Step 1: Theming and Field Styles
   */
  const theme = useContext(ThemeContext);
  const fieldTheme = getFieldThemeObject({
    mode: fieldMode,
    fieldPresentationMode,
    fieldPosition: props,
    fileStyles: fieldDefaultStyles,
    theme: theme?.field,
    sdkTheme: theme
  });

  /**
   * Step 2: Determine file picker configuration. This is primarily used by fields that support file uploads. ie, image, file, table, etc.
   */
  const enableSystemFilePicker = typeof config?.systemFilePicker === 'boolean' ? config?.systemFilePicker : true;

  /**
   * Step 3: Build field props object
   *
   * A table cell mapped onto a page (displayType text, circle, check, ...) is inline editing by
   * definition, so it follows the table's `editability`: when 'inline' is left out it is locked
   * in fill mode. Every other field type gets mappedTableCellLocked = false and is unaffected.
   */
  const isMappedTableCell = type === FieldTypes.table
    && displayType !== FieldDisplayTypes.original
    && displayType !== FieldDisplayTypes.inputGroup
    && displayType !== FieldDisplayTypes.rte;
  const mappedTableCellLocked = isMappedTableCell
    && fieldMode === FieldModes.fill
    && !resolveEditability(editability).inline;

  const fieldProps = {
    id: _id,
    mode: fieldMode,
    fieldId: field,
    fieldIdentifier: identifier,
    fieldPositionId: _id,
    isMobileView: view === FileViews.mobile,
    enableSystemFilePicker,
    ...props,
    ...fieldTheme,
    ...(mappedTableCellLocked ? { disabled: true } : {}),

    /**
     * Blanked last, for the same reason maxLength is blanked below: @emotion/is-prop-valid
     * waves through anything shaped like an event handler, so a field type that spreads its
     * props onto a real DOM node would push this one onto it and warn. The two branches that
     * are meant to have it get it back through autoShrinkProps.
     */
    onAutoHeightChange: undefined,
  };

  /**
   * Step 4: Resolve the auto-shrink gate.
   *
   * <----------------------------NOTICE------------------------->
   * This is the only place the auto-shrink feature is gated by field type, and it has to be
   * here rather than inside TextField. TextField is reached by five routes: this file renders
   * it for text, number and uniqueId, and InputGroupFieldRow, ChartFieldForm and
   * ChartFieldLine all render it directly with an explicit type="text". From inside the
   * component a real text field and a chart axis input are indistinguishable. From here they
   * are not.
   */
  const autoShrinkConfig = resolveAutoShrinkConfig({
    type,
    autoShrink: fieldProps.autoShrink,
    maxLines: fieldProps.maxLines,
    maxLength: fieldProps.maxLength,
  });

  /**
   * Props handed to the text and textarea fields only.
   *
   * maxLength is a real HTML attribute. @emotion/is-prop-valid treats it as forwardable and
   * TextareaField spreads its props straight onto a real textarea, so left alone it would
   * reach the DOM and stop the user typing past it, which is the opposite of what this
   * feature does. Blanking it here, in an object used by the two relevant branches only,
   * avoids removing a prop from under any other field type. maxLines is blanked alongside it
   * for tidiness; it is not a DOM attribute.
   *
   * The gate is passed as three primitives rather than one object, so the shallow prop
   * comparison in these PureComponents is not defeated by a new object every render.
   */
  /**
   * Step 5: Resolve the auto-height gate.
   *
   * <----------------------------NOTICE------------------------->
   * The type gate is the same one auto-shrink uses and is here for the same reason: from
   * inside TextField a real text field and a chart axis input are indistinguishable.
   *
   * In pdf mode the gate opens for float pages only. There the exporter reads this flag to
   * render the field the way the screen does and to measure its overflow (see getExporterFlags
   * in TextField and TextareaField). On a grid page in pdf mode it stays shut: the box is not
   * grown by the auto-height calculator there, baseHeight and baseLineHeight are inert, and
   * only the persisted height and lineHeight matter, as for any doc. One thing does carry over,
   * through autoHeightRequested below: a single-line auto-height field wraps in a grid export
   * whatever its textOverflow, as it does on screen, and is measured at its drawn size like
   * every other wrapping text field (see getExporterFlags in TextField). Gating here, rather
   * than in each consumer, means no shrink, clamp or min-height change can leak into a grid
   * export by accident.
   */
  /**
   * autoHeightRequested is what the document asked for, resolved once so a malformed legacy
   * value (the string "false", say) can never count as a request. autoHeightEnabled is that
   * request after the grid-PDF gate below. TextField needs the first as well as the second:
   * without it an auto-height field on a grid page would count as a plain wrapping field in the
   * PDF but not in the builder, and take two different paths (see resolveIsPlainWrap there).
   */
  const autoHeightRequested = resolveAutoHeightEnabled({ type, autoHeight: fieldProps.autoHeight });

  const autoHeightEnabled = autoHeightRequested
    && (fieldMode !== FieldModes.pdf || pageLayoutMode === PageLayoutModes.float);

  const autoShrinkProps = {
    maxLength: undefined,
    maxLines: undefined,
    autoShrinkEnabled: !!autoShrinkConfig,
    autoShrinkMaxLines: autoShrinkConfig ? autoShrinkConfig.maxLines : undefined,
    autoShrinkMaxLength: autoShrinkConfig ? autoShrinkConfig.maxLength : undefined,

    /**
     * The handler is withheld outright when the gate is closed, and in pdf mode regardless:
     * there is no blur in an export and nothing upstream listening, so it is removed rather
     * than left as a no-op and a stray emission can never reach a page.
     */
    autoHeightEnabled,
    onAutoHeightChange: (autoHeightEnabled && fieldMode !== FieldModes.pdf) ? onAutoHeightChange : undefined,
  };

  const isValid = () => {
    const { type, hidden, hiddenViews, required, requiredLogic, value, schema, displayType, fieldPositionSchema, mode, tableColumns, docFields } = fieldProps;

    const activeField = {
      type,
      hidden,
      hiddenViews,
      required,
      requiredLogic,
      value,
      schema,
      displayType,
      fieldPositionSchema,
      mode,
      tableColumns,
    };

    //Status only: building the per row validities would cost a full pass over large tables for nothing
    return renderValidation.isFieldInvalid(activeField, view, docFields);
  };

  /**
   * Resolve the effective required state, honoring the field's requiredLogic
   * (falls back to the static `required` flag when no logic applies).
   */
  const effectiveRequired = resolveFieldRequired(
    { required, requiredLogic: fieldProps.requiredLogic },
    fieldProps.docFields
  );

  /**
   * For individually-mapped table cells, resolve the effective required from the mapped
   * column (static required + column/cell requiredLogic) against the row's cells, rather
   * than the field-level `required`. Falls back to `required` when column data is absent.
   */
  const effectiveCellRequired = fieldProps.cellColumn
    ? resolveColumnRequired(fieldProps.cellColumn, fieldProps.cellRowCells, fieldProps.docFields)
    : required;

  let fieldNode = null;
  let displayRequiredFieldIndicators = false;

  if (type === FieldTypes.file) {

    displayRequiredFieldIndicators = (effectiveRequired && isValid());

    fieldNode = (
      <StyledField>
        <FileField {...fieldProps} />
      </StyledField>
    );
  }

  if (type === FieldTypes.image) {

    displayRequiredFieldIndicators = (effectiveRequired && isValid());
    fieldNode = (
      <StyledField>
        <ImageField {...fieldProps} layouts={layouts} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.richText) {
    fieldNode = (
      <StyledField>
        <RTEField {...fieldProps} disabled={fieldMode !== FieldModes.edit} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.date) {

    displayRequiredFieldIndicators = (effectiveRequired && isValid());

    fieldNode = (
      <StyledField disabled={disabled}>
        <DateField {...fieldProps} />
      </StyledField>
    );

  }
  else if (
    type === FieldTypes.text
    || type === FieldTypes.number
    || type === FieldTypes.uniqueId
  ) {

    displayRequiredFieldIndicators = (effectiveRequired && isValid());

    /**
     * Wrapping is enabled for the standalone text field only. Number and uniqueId keep their
     * existing single line behaviour, and the chart forms and input group rows that render
     * TextField directly never receive this flag at all.
     */
    const enableValueWrap = type === FieldTypes.text;

    fieldNode = (
      <StyledField disabled={disabled}>
        {/*
          * autoHeightRequested goes on this element only, not through autoShrinkProps: that object
          * is also spread onto TextareaField, which spreads its props onto a real textarea, and an
          * unknown prop there would reach the DOM.
          */}
        <TextField
          {...fieldProps}
          {...autoShrinkProps}
          enableValueWrap={enableValueWrap}
          autoHeightRequested={autoHeightRequested}
        />
      </StyledField>
    );

  }
  else if (type === FieldTypes.rte) {
    displayRequiredFieldIndicators = (effectiveRequired && isValid());
    fieldNode = (
      <StyledField disabled={disabled}>
        <RichTextField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.block) {
    fieldNode = (
      <StyledField>
        {/*
          * tightBoxEnabled is passed here only. TableBlockField (a table column cell dropped
          * on a page, further down) and the table cell renderers reuse BlockField and are
          * deliberately left as they are: the centring rule is for page labels.
          */}
        <BlockField {...fieldProps} tightBoxEnabled />
      </StyledField>
    );
  }
  else if (type === FieldTypes.textarea) {

    displayRequiredFieldIndicators = (effectiveRequired && isValid());

    /**
     * TextareaField is only rendered here, so no other field type can reach it. It still
     * needs the same blanking, because it spreads its props onto a real textarea. It gets no
     * enableValueWrap, since a textarea already wraps.
     */
    fieldNode = (
      <StyledField disabled={disabled}>
        <TextareaField {...fieldProps} {...autoShrinkProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.chart) {

    displayRequiredFieldIndicators = (effectiveRequired && isValid());

    fieldNode = (
      <StyledField>
        <ChartField
          {...fieldProps}
        />
      </StyledField>
    );
  }
  else if (type === FieldTypes.signature) {

    displayRequiredFieldIndicators = (effectiveRequired && isValid());

    fieldNode = (
      <StyledField>
        <SignatureField {...fieldProps} disabled={fieldProps.disabled || fieldMode === FieldModes.edit} />
      </StyledField>
    );
  }

  /**
   * Dropdown Fields
   */
  else if (
    type === FieldTypes.dropdown
    && (displayType === FieldDisplayTypes.original || displayType === FieldDisplayTypes.text)
  ) {

    displayRequiredFieldIndicators = (effectiveRequired && isValid());

    fieldNode = (
      <StyledField disabled={disabled}>
        <DropdownField {...fieldProps} />
      </StyledField>
    );
  }
  else if (
    type === FieldTypes.dropdown
    && (displayType !== FieldDisplayTypes.original && displayType !== FieldDisplayTypes.text)
  ) {

    if (fieldMode === FieldModes.edit) fieldProps.value = fieldProps.targetValue;

    displayRequiredFieldIndicators = (effectiveRequired && isValid());

    fieldNode = (
      <StyledField>
        <DropdownSelectionField
          {...fieldProps}
          disabled={disabled || fieldMode === FieldModes.edit}
        />
      </StyledField>
    );
  }

  /**
   * Multi-Select Fields
   */
  else if (type === FieldTypes.multiSelect
    && (displayType === FieldDisplayTypes.original || displayType === FieldDisplayTypes.horizontal)) {

    displayRequiredFieldIndicators = (effectiveRequired && isValid());

    fieldNode = (
      <StyledField>
        <MultiSelectField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.multiSelect && displayType !== FieldDisplayTypes.original) {

    if (fieldMode === FieldModes.edit) fieldProps.value = fieldProps.targetValue;

    displayRequiredFieldIndicators = (effectiveRequired && isValid());

    fieldNode = (
      <StyledField>
        <MultiSelectSelectionField
          {...fieldProps}
          disabled={disabled || fieldMode === FieldModes.edit}
          value={fieldProps.value || fieldMode === FieldModes.edit}
        />
      </StyledField>
    );
  }

  /**
   * Pricing Table Field
   */
  else if (type === FieldTypes.pricingTable) {
    displayRequiredFieldIndicators = (effectiveRequired && isValid());

    fieldNode = (
      <StyledField>
        <PricingTableField {...fieldProps} />
      </StyledField>
    );
  }

  /**
   * Collection Field
   */
  else if (type === FieldTypes.collection) {

    displayRequiredFieldIndicators = (effectiveRequired && isValid());

    if (!licenseValidation
      || !licenseValidation.isValid
      || (licenseValidation && !licenseValidation.decodedData.collectionField)
    ) {
      return null;
    }

    fieldNode = (
      <StyledField>
        <CollectionField
          {...fieldProps}
          theme={theme}
          layouts={layouts}
          disabled={disabled}
        />
      </StyledField>
    );

  }

  /**
   * Table Fields
   */
  else if (type === FieldTypes.table && (displayType === FieldDisplayTypes.original || displayType === FieldDisplayTypes.inputGroup || displayType === FieldDisplayTypes.rte)) {

    displayRequiredFieldIndicators = (effectiveRequired && isValid());

    fieldNode = (
      <StyledField>
        <TableFieldv2
          {...fieldProps}
          layouts={layouts}
          disabled={disabled}
        />

      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.text) {

    displayRequiredFieldIndicators = (effectiveCellRequired && isFieldEmpty(columnType, cellValue));

    fieldNode = (
      <StyledField disabled={fieldProps.disabled}>
        <TableTextField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.barcode) {

    displayRequiredFieldIndicators = (effectiveCellRequired && isFieldEmpty(columnType, cellValue));

    fieldNode = (
      <StyledField disabled={fieldProps.disabled}>
        <TableBarcodeField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.signature) {

    displayRequiredFieldIndicators = (effectiveCellRequired && isFieldEmpty(columnType, cellValue));

    fieldNode = (
      <StyledField disabled={fieldProps.disabled}>
        <TableSignatureField
          {...fieldProps}
          disabled={fieldProps.disabled || fieldMode === FieldModes.edit}
        />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.number) {

    displayRequiredFieldIndicators = (effectiveCellRequired && isFieldEmpty(columnType, cellValue));

    fieldNode = (
      <StyledField disabled={fieldProps.disabled}>
        <TableNumberField {...fieldProps} />
      </StyledField>
    );

  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.image) {

    displayRequiredFieldIndicators = (effectiveCellRequired && isFieldEmpty(columnType, cellValue));

    fieldNode = (
      <StyledField>
        <TableImageField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.dropdown) {

    displayRequiredFieldIndicators = (effectiveCellRequired && isFieldEmpty(columnType, cellValue, options));

    fieldNode = (
      <StyledField disabled={fieldProps.disabled}>
        <TableDropdownField {...fieldProps} />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.multiSelect) {

    displayRequiredFieldIndicators = (effectiveCellRequired && isFieldEmpty(columnType, cellValue, options));

    fieldNode = (
      <StyledField disabled={fieldProps.disabled}>
        <TableMultiselectField {...fieldProps} />
      </StyledField>
    );
  }
  else if (
    type === FieldTypes.table
    && columnType === FieldTableColumnTypes.dropdown
    && displayType !== FieldDisplayTypes.original
    && displayType !== FieldDisplayTypes.text
  ) {

    if (fieldMode === FieldModes.edit) fieldProps.cellValue = fieldProps.targetValue;

    displayRequiredFieldIndicators = (effectiveCellRequired && isFieldEmpty(columnType, cellValue, options));

    fieldNode = (
      <StyledField>
        <TableDropdownSelectionField
          {...fieldProps}
          disabled={fieldProps.disabled || fieldMode === FieldModes.edit}
        />
      </StyledField>
    );
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.date) {

    displayRequiredFieldIndicators = (effectiveCellRequired && isFieldEmpty(columnType, cellValue));

    fieldNode = (
      <StyledField disabled={fieldProps.disabled}>
        <TableDateField {...fieldProps} />
      </StyledField>
    );

  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text && columnType === FieldTableColumnTypes.block) {

    displayRequiredFieldIndicators = (effectiveCellRequired && isFieldEmpty(columnType, cellValue, options));

    fieldNode = (
      <StyledField disabled={fieldProps.disabled}>
        <TableBlockField {...fieldProps} />
      </StyledField>
    );
  }

  /**
   * A mobile view mirroring desktop renders with fieldMode forced to readonly so the
   * canvas stays non-interactive, while the author is still editing. Indicators follow
   * the authoring mode; everything else in this file stays on fieldMode. Falls back to
   * fieldMode for direct consumers that pass no indicatorMode, such as the PDF exporter.
   */
  const effectiveIndicatorMode = indicatorMode || fieldMode;

  const indicatorNodes = (
    <StyledFieldIndicatorWrapper>
      {!displayRequiredFieldIndicators || fieldMode === FieldModes.pdf || effectiveIndicatorMode === FieldModes.readonly ? null : (
        <StyledFieldIndicator>
          <Icon icon={ThemeIcons.settings.required} color={theme.dangerColor} />
        </StyledFieldIndicator>
      )}
      {effectiveIndicatorMode !== FieldModes.edit ? null : (
        (hidden || isFieldHiddenInView(hiddenViews, view)) ? (
          <StyledFieldIndicator>
            <Icon icon={ThemeIcons.settings.hide} color={theme.warningColor} />
          </StyledFieldIndicator>
        ) : null
      )}
    </StyledFieldIndicatorWrapper>
  );

  return (
    <StyledFieldWrapper {...{ disabled }}>
      {indicatorNodes}
      {fieldNode}
    </StyledFieldWrapper>
  );

};

export default memo(Field);
